import { unfoldIcs } from './icsUnfold';

/**
 * @typedef {'session' | 'assignment' | 'other'} LmsCalendarEventKind
 */

/**
 * @typedef {Object} LmsCalendarEvent
 * @property {string} uid
 * @property {string} title
 * @property {string} [description]
 * @property {string} [location]
 * @property {string} [url]
 * @property {boolean} allDay
 * @property {string} start ISO 8601 datetime (e.g. …Z) or calendar date YYYY-MM-DD for all-day
 * @property {string | null} end same format, or null if absent
 * @property {LmsCalendarEventKind} kind inferred from UID prefix (Canvas/VIT LMS)
 */

/**
 * @param {string} uid
 * @returns {LmsCalendarEventKind}
 */
export function inferEventKind(uid) {
    if (!uid) return 'other';
    if (uid.startsWith('event-calendar-event-')) return 'session';
    if (uid.startsWith('event-assignment-')) return 'assignment';
    return 'other';
}

/**
 * Minimal unescaping for iCalendar TEXT values.
 * @param {string} value
 * @returns {string}
 */
function unescapeIcsText(value) {
    if (!value) return '';
    return value
        .replace(/\\n/gi, '\n')
        .replace(/\\N/g, '\n')
        .replace(/\\,/g, ',')
        .replace(/\\;/g, ';')
        .replace(/\\\\/g, '\\');
}

/**
 * @param {string} rawValue e.g. 20260314T083000Z or 20260322
 * @param {string[]} paramParts e.g. ['VALUE=DATE','VALUE=DATE']
 * @returns {{ allDay: boolean, iso: string } | null}
 */
function parseIcsDateTime(rawValue, paramParts) {
    const raw = (rawValue || '').trim();
    if (!raw) return null;

    const paramUpper = paramParts.join(';').toUpperCase();
    const valueDate = paramUpper.includes('VALUE=DATE');
    const eightDigits = /^\d{8}$/.test(raw);

    if (valueDate || eightDigits) {
        if (raw.length === 8 && /^\d{8}$/.test(raw)) {
            const y = raw.slice(0, 4);
            const mo = raw.slice(4, 6);
            const d = raw.slice(6, 8);
            return { allDay: true, iso: `${y}-${mo}-${d}` };
        }
    }

    const m = raw.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z?)$/);
    if (m) {
        const [, y, mo, day, h, mi, s, z] = m;
        const iso = z === 'Z' ? `${y}-${mo}-${day}T${h}:${mi}:${s}Z` : `${y}-${mo}-${day}T${h}:${mi}:${s}`;
        return { allDay: false, iso };
    }

    // DATE-only without explicit VALUE=DATE (unusual)
    if (raw.length === 8 && /^\d{8}$/.test(raw)) {
        const y = raw.slice(0, 4);
        const mo = raw.slice(4, 6);
        const d = raw.slice(6, 8);
        return { allDay: true, iso: `${y}-${mo}-${d}` };
    }

    return null;
}

/**
 * @param {Record<string, string | { raw: string, params: string[] }>} raw
 * @returns {LmsCalendarEvent | null}
 */
function buildEvent(raw) {
    const uid = typeof raw.UID === 'string' ? raw.UID.trim() : '';

    const title = unescapeIcsText(typeof raw.SUMMARY === 'string' ? raw.SUMMARY.trim() : '');
    if (!title && !uid) return null;

    const dtStartField = raw.DTSTART;
    const dtEndField = raw.DTEND;

    const startParsed =
        dtStartField && typeof dtStartField === 'object'
            ? parseIcsDateTime(dtStartField.raw, dtStartField.params)
            : null;

    if (!startParsed) return null;

    let endParsed =
        dtEndField && typeof dtEndField === 'object'
            ? parseIcsDateTime(dtEndField.raw, dtEndField.params)
            : null;

    if (!endParsed && startParsed.allDay) {
        endParsed = null;
    }

    /** @type {LmsCalendarEvent} */
    const ev = {
        uid,
        title: title || '(No title)',
        allDay: startParsed.allDay,
        start: startParsed.iso,
        end: endParsed ? endParsed.iso : null,
        kind: inferEventKind(uid),
    };

    if (typeof raw.DESCRIPTION === 'string' && raw.DESCRIPTION.trim()) {
        ev.description = unescapeIcsText(raw.DESCRIPTION.trim());
    }
    if (typeof raw.LOCATION === 'string' && raw.LOCATION.trim()) {
        ev.location = unescapeIcsText(raw.LOCATION.trim());
    }
    if (typeof raw.URL === 'string' && raw.URL.trim()) {
        ev.url = unescapeIcsText(raw.URL.trim());
    }

    return ev;
}

/**
 * Parse unfolded content lines inside a VEVENT into a property bag.
 * @param {string} namePart before first colon (e.g. DTSTART;VALUE=DATE or URL;VALUE=URI)
 * @param {string} value after first colon
 * @param {Record<string, unknown>} bag
 */
function accumulateProperty(namePart, value, bag) {
    const segments = namePart.split(';');
    const propName = segments[0].toUpperCase();
    const params = segments.slice(1);

    if (propName === 'DTSTART' || propName === 'DTEND') {
        bag[propName] = { raw: value, params };
        return;
    }

    const prev = bag[propName];
    if (prev === undefined) {
        bag[propName] = value;
    } else if (typeof prev === 'string') {
        bag[propName] = prev + value;
    }
}

/**
 * Parse iCalendar (ICS) text into calendar metadata and normalized events.
 *
 * @param {string} text raw .ics file contents (folded or unfolded)
 * @returns {{ calendarName: string | null, events: LmsCalendarEvent[] }}
 */
export function parseIcs(text) {
    const unfolded = unfoldIcs(text);
    const lines = unfolded.split(/\r?\n/);

    /** @type {string | null} */
    let calendarName = null;
    /** @type {LmsCalendarEvent[]} */
    const events = [];
    /** @type {Record<string, unknown> | null} */
    let current = null;

    for (const line of lines) {
        if (line === 'BEGIN:VEVENT') {
            current = {};
            continue;
        }
        if (line === 'END:VEVENT') {
            if (current) {
                const ev = buildEvent(/** @type {Record<string, string | { raw: string, params: string[] }>} */ (current));
                if (ev) events.push(ev);
            }
            current = null;
            continue;
        }

        if (line.startsWith('X-WR-CALNAME:')) {
            calendarName = line.slice('X-WR-CALNAME:'.length).trim() || null;
            continue;
        }

        if (!current) continue;

        const m = line.match(/^([^:]+):(.*)$/);
        if (!m) continue;
        accumulateProperty(m[1], m[2], current);
    }

    return { calendarName, events };
}
